import clsx from "clsx";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import Icon, { type IconName } from "../components/Icon";
import { IconButton } from "./Button";
import { useDialog } from "./useDialog";

/* ==========================================================================
   Модальное окно
   ========================================================================== */

const widths = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  full: "max-w-[min(72rem,95vw)]",
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  /** Убирает крестик и закрытие по фону — для действий, требующих ответа. */
  dismissable = true,
  /** Доступное имя окна, когда заголовка нет. */
  label,
  /** Селектор элемента, который получает фокус при открытии: по умолчанию
   *  первый элемент управления (у окна с заголовком — крестик). */
  initialFocus,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof widths;
  dismissable?: boolean;
  label?: string;
  initialFocus?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(box, { open, onClose, dismissable, initialFocus });

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      <div
        className="fixed inset-0 bg-overlay/45 backdrop-blur-[2px]"
        onClick={dismissable ? onClose : undefined}
      />
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : label}
        className={clsx(
          "relative my-auto w-full animate-scale-in rounded-xl border border-line bg-surface shadow-xl",
          widths[size],
        )}
      >
        {(title || dismissable) && (
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-3.5">
            <div className="min-w-0">
              {title && <h2 id={titleId} className="text-md font-semibold text-ink">{title}</h2>}
              {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
            </div>
            {dismissable && (
              <IconButton icon="close" label="Закрыть" size="sm" onClick={onClose} className="-mr-1.5 -mt-0.5" />
            )}
          </div>
        )}
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-sunken/60 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ==========================================================================
   Выпадающее меню
   ========================================================================== */

export interface MenuItem {
  label: ReactNode;
  icon?: IconName;
  onSelect?: () => void;
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Разделитель перед пунктом. */
  separated?: boolean;
}

export function Menu({
  trigger,
  items,
  align = "end",
  side = "bottom",
  className,
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  items: MenuItem[];
  align?: "start" | "end";
  /** «top» — для триггеров у нижнего края экрана, чтобы меню не ушло за него. */
  side?: "bottom" | "top";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = useCallback(() => setOpen((value) => !value), []);

  return (
    <div ref={box} className={clsx("relative", className)}>
      {trigger({ open, toggle })}
      {open && (
        <div
          role="menu"
          className={clsx(
            "absolute z-drawer min-w-[11rem] animate-scale-in rounded-lg border border-line",
            "bg-raised p-1 shadow-lg",
            align === "end" ? "right-0" : "left-0",
            side === "top" ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          {items.map((item, index) => {
            const body = (
              <>
                {item.icon && <Icon name={item.icon} size={14} />}
                <span className="truncate">{item.label}</span>
              </>
            );
            const classes = clsx(
              "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors",
              item.disabled
                ? "cursor-not-allowed text-faint"
                : item.danger
                  ? "text-danger hover:bg-danger-soft"
                  : "text-ink hover:bg-sunken",
            );
            return (
              <div key={index}>
                {item.separated && <div className="my-1 h-px bg-line" />}
                {item.to ? (
                  <a href={item.to} role="menuitem" className={classes} onClick={() => setOpen(false)}>
                    {body}
                  </a>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    className={classes}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect?.();
                    }}
                  >
                    {body}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   Подсказка
   ========================================================================== */

export function Tooltip({
  content,
  children,
  side = "top",
}: {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom";
}) {
  const [shown, setShown] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShown(true)}
      onMouseLeave={() => setShown(false)}
      onFocus={() => setShown(true)}
      onBlur={() => setShown(false)}
    >
      {children}
      {shown && (
        <span
          role="tooltip"
          className={clsx(
            "pointer-events-none absolute left-1/2 z-modal w-max max-w-xs -translate-x-1/2",
            "animate-fade-in rounded-md border border-line bg-raised px-2 py-1",
            "text-xs font-normal leading-4 text-ink shadow-md",
            side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
}

/* ==========================================================================
   Боковая панель
   ========================================================================== */

export function Sheet({
  open,
  onClose,
  title,
  children,
  width = "28rem",
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: string;
}) {
  const panel = useRef<HTMLElement>(null);
  const titleId = useId();
  useDialog(panel, { open, onClose });

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-drawer">
      <div className="absolute inset-0 bg-overlay/40" onClick={onClose} />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute right-0 top-0 flex h-full max-w-[92vw] animate-slide-in-right flex-col border-l border-line bg-surface shadow-xl"
        style={{ width }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 id={titleId} className="text-md font-semibold text-ink">{title}</h2>
          <IconButton icon="close" label="Закрыть" size="sm" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4">{children}</div>
      </aside>
    </div>,
    document.body,
  );
}

/** Позиционирование поповера рядом с якорем — для командной палитры и меню. */
export function useAnchoredPosition(anchor: HTMLElement | null, open: boolean) {
  const [style, setStyle] = useState<React.CSSProperties>({});
  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    setStyle({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width });
  }, [anchor, open]);
  return style;
}
